import React from 'react';
import Header from './Header';
import background from './img/softwarePartners.png';
import MobileNavigation from './MobileNavigation';
import { CopyBlock, dracula } from 'react-code-blocks';
import Footer from './Footer';

function DeveloperGlossary() {
  return (
    <>
      <MobileNavigation />
      <section className="bg-[#212121]" id="top">
        <div
          className="w-full pb-28"
          style={{
            backgroundImage: `url(${background})`,
            backgroundSize: '200% 200%',
            backgroundPosition: 'bottom center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <Header />
          <div className="p-4 row md:p-8 how">
            <h3 className="text-white large">Glossary</h3>
          </div>
          <div className="flex p-16 row flex-col justify-center flex-start gap-6 self-stretch rounded-[28px] bg-white !mb-10">
            <p className="text-black light">
              A glossary for anything and everything about Basys' APIs
            </p>
            <h2 className="text-black large">API Reference</h2>
            <p className="text-black light">
              The Basys API is organized around REST. Our API has predictable resource-oriented
              URLs, accepts form-encoded request bodies, returns JSON-encoded responses, and uses
              standard HTTP response codes, authentication, and verbs.
            </p>
            <h2 className="text-black large">Authentication</h2>
            <p className="text-black light">
              Basys' APIs follows the OAuth 2.0 authentication protocol by utilizing JSON Web Tokens
              (JWT) to authenticate requests. To generate a JWT see the Getting Started page. These
              JWTs allow you access Basys' APIs which can submit invoices, add customers and process
              transactions so keep them save and secure! Do not share your JWT Tokens and the
              client_ids and secretIds that generate them in publicly accessible areas such as
              GitHub, client-side code, or anywhere they can be easily copied from.
            </p>

            <p className="text-black light">
              All API requests must be made over HTTPS. Calls made over plain HTTP will fail. API
              requests without authentication will also fail.
            </p>
            <h2 className="text-black large">API Definitions & Statuses</h2>
            <h2 className="text-black">HTTP Methods:</h2>
            <p className="text-black light">
              HTTP methods enable API clients to perform CRUD (Create, Read, Update, and Delete)
              actions on an API’s resources in a standardized and predictable way. The following
              describes each HTTP Method:
            </p>
            <h2 className="text-xl text-black">GET</h2>
            <p className="text-black light">
              The GET method is used to retrieve data on a server. Clients can use the GET method to
              access all of the resources of a given type, or they can use it to access a specific
              resource. For instance, a GET request to the /gateway endpoint of the Gateway API
              would return all of the gateways in the database, while a GET request to the
              /gateway/123 endpoint would return the specific Gateway with an ID of 123. GET
              requests typically do not include a request body, as the client is not attempting to
              create or update data.
            </p>
            <h2 className="text-xl text-black">POST</h2>
            <p className="text-black light">
              The POST method is used to create new resources. For instance, if the client wanted to
              add a new gateway to the database, they would send a POST request to the /gateway
              endpoint. Unlike GET requests, POST requests typically include a request body, which
              is where the client specifies the attributes of the resource to be created. For
              example, a POST request to the /gateway endpoint might have a request body that looks
              like this:
            </p>
            <CopyBlock
              text={`{
"name": "string", 
"description": "string", 
"phone": "string", 
"phoneExternal": "string", 
"receiptEmail": "string", 
"website": "string", 
"timezone": "string", 
"merchantId": "string", 
"addresses": [ 
    { 
       "isPrimary": true, 
       "isBilling": true, 
       "isInvoice": true, 
       "address": { 
              "addressLine1": "string", 
              "addressLine2": "string", 
              "city": "string", 
              "state": "string", 
              "postalCode": "string", 
              "country": "string", 
              "firstName": "string", 
              "lastName": "string", 
              "email": "user@example.com", 
              "phone": "2096087464", 
              "fax": "5273006741", 
              "company": "string" 
       } 
    } 
], 
"gatewayMetadata": [ 
    { 
       "preferenceType": "string", 
       "gatewaySettings": [ 
           { 
              "code": "string", 
              "value": "string" 
           } 
        ] 
    } 
], 
"gatewayConfigurations": [ 
    { 
    "categoryCode": "string", 
    "fieldEntryValues": [ 
        { 
            "fieldCode": "string", 
            "fieldValue": [ 
                "string" 
                ] 
            } 
        ] 
    } 
] 
}`}
              theme={dracula}
              language="JSON"
              showLineNumbers={true}
              customStyle={{ overflowX: 'auto' }}
            />
            <h2 className="text-xl text-black">PUT</h2>
            <p className="text-black light">
              The PUT method is used to replace an existing resource with an updated version. This
              method works by replacing the entire resource (i.e., the specific gateway located at
              the /gateway/123/configuration endpoint) with the data that is included in the
              request’s body. This means that any fields or properties not included in the request
              body are deleted, and any new fields or properties are added.
            </p>
            <h2 className="text-xl text-black">PATCH</h2>
            <p className="text-black light">
              The PATCH method is used to update an existing resource. It is similar to PUT, except
              that PATCH enables clients to update specific properties on a resource—without
              overwriting the others. For instance, if you have a gateway resource with fields for
              name, andaddress, but you only want to update the address, you could use the PATCH
              method to send a request that only includes the new value for the address field. The
              rest of the resource would remain unchanged. This behavior makes the PATCH method more
              flexible and efficient than PUT.
            </p>
            <h2 className="text-xl text-black">DELETE</h2>
            <p className="text-black light">
              The DELETE method is used to remove data from a database. When a client sends a DELETE
              request, it is requesting that the resource at the specified URL be removed. For
              example, a DELETE request to the /gateway/123 endpoint will permanently remove the
              gateway with an ID of 123 from the database. Some APIs may leverage authorization
              mechanisms to ensure that only clients with the appropriate permissions are able to
              delete resources.
            </p>
            <h2 className="text-xl text-black">HTTP Status Codes</h2>
            <table className="tg" style={{ undefined, tableLayout: 'fixed', width: '100%' }}>
              <colgroup>
                <col style={{ width: '25%' }} />
                <col style={{ width: '25%' }} />
                <col style={{ width: '25%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th className="font-bold text-center text-black">Code</th>
                  <th className="font-bold text-center text-black">Response</th>
                  <th className="font-bold text-center text-black">Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>200</td>
                  <td>Success</td>
                  <td>Everything worked as expected.</td>
                </tr>
                <tr>
                  <td>201</td>
                  <td>Created</td>
                  <td>A new resource was successfully created.</td>
                </tr>
                <tr>
                  <td>204</td>
                  <td>No Content</td>
                  <td>
                    A resource was successfully processed and no content is returned. Happens when a
                    resources is deleted.
                  </td>
                </tr>
                <tr>
                  <td>207</td>
                  <td>Sucess</td>
                  <td>Multiple Resources were updated successfully.</td>
                </tr>
                <tr>
                  <td>400</td>
                  <td>Bad Request</td>
                  <td>The request was unacceptable, often due to missing a required parameter.</td>
                </tr>
                <tr>
                  <td>401</td>
                  <td>Unauthorized</td>
                  <td>No valid JWT provided.</td>
                </tr>
                <tr>
                  <td>403</td>
                  <td>Forbidden</td>
                  <td>The JWT doesn’t have permissions to perform the request.</td>
                </tr>
                <tr>
                  <td>404</td>
                  <td>Not Found</td>
                  <td>The requested resource doesn’t exist.</td>
                </tr>
                <tr>
                  <td>409</td>
                  <td>Conflict</td>
                  <td>
                    The request conflicts with another request (perhaps due to using the same
                    idempotent key).
                  </td>
                </tr>
                <tr>
                  <td>422</td>
                  <td>Client Error</td>
                  <td>Unable to process the request because it contains invalid data.</td>
                </tr>
                <tr>
                  <td>429</td>
                  <td>Too Many Requests</td>
                  <td>
                    Too many requests hit the API too quickly. We recommend an exponential backoff
                    of your requests.
                  </td>
                </tr>
                <tr>
                  <td>500, 502, 503, 504</td>
                  <td>Server Error</td>
                  <td>Something went wrong when trying to make a call to the server.</td>
                </tr>
              </tbody>
            </table>
            <h2 className="text-black large">Credit Card Processing Technology Terms</h2>
            <ul className="ml-10 list-disc">
              <li className="text-black light">
                ACH: Electronic network for domestic low-value payments between financial
                institutions.
              </li>
              <li className="text-black light">
                Authorization: Confirms sufficient funds for a transaction.
              </li>
              <li className="text-black light">
                Blind Credit: Refund issued without an associated transaction.
              </li>
              <li className="text-black light">
                Captured Amount: Final amount collected during settlement.
              </li>
              <li className="text-black light">
                Capture: Marks an authorized transaction for settlement.
              </li>
              <li className="text-black light">CSV: Comma-separated values file format.</li>
              <li className="text-black light">
                Customer Payable Token: Encrypted ID for customers to view outstanding invoices.
              </li>
              <li className="text-black light">
                Gateway: Platform that transmits payment data to the processor.
              </li>
              <li className="text-black light">
                Idempotency: Property ensuring repeated requests yield the same result.
              </li>
              <li className="text-black light">
                Incremental Authorization: Additional authorized amount to an existing one.
              </li>
              <li className="text-black light">
                Invoice: Billing statement to a customer for payment.
              </li>
              <li className="text-black light">
                JWT: JSON Web Token used for secure data exchange.
              </li>
              <li className="text-black light">
                Processor: Facilitates fund transfers between cardholder and merchant banks.
              </li>
              <li className="text-black light">
                Refund: Returns captured amount to the cardholder.
              </li>
              <li className="text-black light">
                Sale: Transaction type flagged for automatic settlement.
              </li>
              <li className="text-black light">
                Settlement: Transfer of funds from cardholder’s bank to merchant’s.
              </li>
              <li className="text-black light">Subscriptions: Recurring automatic payments.</li>
              <li className="text-black light">
                Tokenization: Replacing sensitive data with nonsensitive tokens.
              </li>
              <li className="text-black light">
                Transaction: Any billable event under a merchant's account.
              </li>
              <li className="text-black light">
                Triggers: Hard-coded codes generating predetermined responses.
              </li>
              <li className="text-black light">
                Validation: Checks credit card or ACH account validity.
              </li>
              <li className="text-black light">
                Virtual Terminal: POS UI for processing credit card/ACH payments.
              </li>
              <li className="text-black light">Void: Cancels a transaction before settlement.</li>
              <li className="text-black light">
                Webhooks: API callbacks notifying other systems of events.
              </li>
            </ul>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
}

export default DeveloperGlossary;
